import {priority, common} from '@priority-software/priority-proto-api/lib';
import {
    MANDATORY_COLS,
    storageKey,
    defaultCols,
    COMPLETED_FIELD,
    STATUSES_DROPDOWN_MAX_HEIGHT,
    DROPDOWN_INPUT_HEIGHT
} from './consts';
import {getLang, isRTL} from '../../Localization';
import {Column, Record} from '../../stores/Form';
import {getDateObjectFromField} from '../../features/Form/utils/date';
import {setServerStoreData, getServerStoreData} from '../../services/proto/ServerStorage/service';
import {TooltipPosition} from '../../components/Common/Tooltip/types';

export type MandatoryColIds = {
    [key: string]: number;
};

type DropdownContentStyleParams = {
    fieldClientRect: DOMRect;
    rtl: boolean;
    scrollAreaMaxHeight?: number;
};

export const getColIdByName = (colName: string, cols: priority.netitems.form.IColumn[]) => {
    const col = cols.find(col => col.Name === colName);
    return col?.ID;
};
export const getMandatoryColIds = (cols: priority.netitems.form.IColumn[]): MandatoryColIds => {
    const mandatoryColIds = MANDATORY_COLS.reduce((acc, colName) => {
        const col = cols.find(col => col.Name === colName);
        acc[colName] = col?.ID;
        return acc;
    }, {});

    return mandatoryColIds;
};

export const getViewColsData = (
    cols: priority.netitems.form.IColumn[],
    viewCols: string[]
): priority.netitems.form.IColumn[] => {
    const viewColsData = [];
    viewCols?.forEach(colName => {
        const col = cols.find(col => col.Name === colName);
        col && viewColsData.push(col);
    });

    return viewColsData;
};

export const getFieldValue = (record: Record, id: number) => {
    const field = record.getField(id);
    return field?.origValue;
};

export const getIsOverdue = ({record, column}) => {
    const field = record.getField(column.id);
    const dateInRecord = getDateObjectFromField(field);
    if (dateInRecord) {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        return now > dateInRecord;
    }
    return false;
};

export const getIsSingleWord = (value: string) => {
    return value?.trim().indexOf(' ') === -1;
};

export const getScrollAreaMaxHeight = windowInnerHeight => {
    const menuHeaderHeight = 80;
    const triggerHeight = 23;

    const scrollAreaMaxHeight =
        (windowInnerHeight - menuHeaderHeight - triggerHeight) / 2 - DROPDOWN_INPUT_HEIGHT;
    return scrollAreaMaxHeight > STATUSES_DROPDOWN_MAX_HEIGHT
        ? STATUSES_DROPDOWN_MAX_HEIGHT
        : scrollAreaMaxHeight;
};

export const getDropdownContentStyle = ({
    fieldClientRect,
    scrollAreaMaxHeight,
    rtl
}: DropdownContentStyleParams) => {
    const top = fieldClientRect.top - (window.innerHeight - fieldClientRect.height / 2);

    const verticalPosition =
        Math.abs(top) < scrollAreaMaxHeight + DROPDOWN_INPUT_HEIGHT
            ? {bottom: Math.abs(top)}
            : {top};

    const horizontalPosition = rtl
        ? {right: window.innerWidth - fieldClientRect.right}
        : {left: fieldClientRect.left};
    return {...verticalPosition, ...horizontalPosition};
};

export const getAvailableCols = (cols: Column[]) => {
    return cols
        .filter(col => col.Name !== COMPLETED_FIELD)
        .filter(col => !MANDATORY_COLS.includes(col.Name));
};

export const saveWidgetViewColumns = async (selectedFields: Array<string>) => {
    const key = `${storageKey}_${getLang()}`;
    const selectedFieldsToSave = new common.Constant({Str: JSON.stringify(selectedFields)});
    const fieldsStr = {[key]: selectedFieldsToSave};
    try {
        await setServerStoreData(fieldsStr, true, false);
    } catch (err) {
        console.log('An error occurred while saving updated todolist widget view columns', err);
    }
};

export const getWidgetViewColumns = async () => {
    const key = `${storageKey}_${getLang()}`;
    try {
        const storedColumnNames = await getServerStoreData([key], false, true);
        return storedColumnNames[key] ? JSON.parse(storedColumnNames[key].Str) : defaultCols;
    } catch (e) {
        console.error(
            'An error occurred while getting todolist widget view columns',
            (e as Error).message
        );
    }
};

export const getTooltipPosition = (recordsCount: number = 0): TooltipPosition => {
    const dirRtl = isRTL();
    if (recordsCount === 1) {
        return dirRtl ? 'left center' : 'right center';
    }
    return dirRtl ? 'top right' : 'top left';
};
